import type { FunctionComponent } from 'react';
import { useCallback } from 'react';

import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { Icon } from '../../../shared/components/icon/icon';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';

import styles from './toolbar.module.scss';

const style = createStyleHelper(styles, 'toolbar');

export interface IToolbarProps {
    className?: string;
    onClose?: () => void;
    onSlotCountChange?: (count: number) => void;
    activeSlotCount: number;
    isFullscreen: boolean;
    isSearchActive: boolean;
    setFullscreen: (fullscreen: boolean) => void;
    toggleSearch?: (count: number) => void;
}

export const Toolbar: FunctionComponent<IToolbarProps> = ({
    className,
    onClose,
    onSlotCountChange,
    activeSlotCount,
    isFullscreen,
    setFullscreen,
    toggleSearch,
    isSearchActive,
}) => {
    const toggleFullscreen = useCallback(() => {
        setFullscreen(!isFullscreen);
    }, [isFullscreen, setFullscreen]);

    return (
        <nav className={createClassName([style(), className])}>
            <button
                onClick={() => toggleSearch?.(0)}
                className={style('nav-item', { active: isSearchActive })}
            >
                <Icon className={style('icon')} name={'search'} />
                <span className={style('label')}>
                    <FormattedMessage
                        id={'gameplay-toolbar.search.label'}
                        defaultMessage={'Search'}
                    />
                </span>
            </button>

            <button onClick={toggleFullscreen} className={style('nav-item')}>
                <Icon
                    className={style('icon')}
                    name={isFullscreen ? 'compress-arrows' : 'expand-arrows'}
                />

                <span className={style('label')}>
                    {/** Display both so width is always the same as largest text */}
                    <span className={style('fullscreen', ['minimize'])}>
                        <FormattedMessage
                            id={'gameplay-toolbar.fullscreen.minimize.label'}
                            defaultMessage={'Minimize'}
                        />
                    </span>
                    <span className={style('fullscreen', ['maximize'])}>
                        <FormattedMessage
                            id={'gameplay-toolbar.fullscreen.maximize.label'}
                            defaultMessage={'Full Screen'}
                        />
                    </span>
                </span>
            </button>

            <button
                className={style('nav-item', { active: activeSlotCount === 1 })}
                onClick={() => onSlotCountChange?.(1)}
            >
                <Icon className={style('icon')} name={'square'} />
                <span className={style('label')}>
                    <FormattedMessage
                        id={'gameplay-toolbar.matrix-controls.1x.label'}
                        defaultMessage={'1x'}
                    />
                </span>
            </button>

            <button
                className={style('nav-item', { active: activeSlotCount === 2 })}
                onClick={() => onSlotCountChange?.(2)}
            >
                <Icon className={style('icon')} name={'square'} />
                <span className={style('label')}>
                    <FormattedMessage
                        id={'gameplay-toolbar.matrix-controls.2x.label'}
                        defaultMessage={'2x'}
                    />
                </span>
            </button>

            <button
                className={style('nav-item', { active: activeSlotCount === 4 })}
                onClick={() => onSlotCountChange?.(4)}
            >
                <Icon className={style('icon')} name={'square'} />
                <span className={style('label')}>
                    <FormattedMessage
                        id={'gameplay-toolbar.matrix-controls.4x.label'}
                        defaultMessage={'4x'}
                    />
                </span>
            </button>

            <button className={style('nav-item')} onClick={onClose}>
                <Icon className={style('icon')} name={'times'} />
                <span className={style('label')}>
                    <FormattedMessage
                        id={'gameplay-toolbar.close.label'}
                        defaultMessage={'Close'}
                    />
                </span>
            </button>
        </nav>
    );
};
